import React, { useState, useRef, memo, useMemo } from 'react';
import JoditEditor from 'jodit-react';

import Button from 'src/components/helpers/Button';
import Popup from 'src/components/helpers/Popup';
import { usePopupContext } from '../../../contexts/PopupContext';

import style from './style.module.css';

const DROPDOWN_LIST = [
  { listName: 'moduleFirstName', name: 'Module First Name', value: '{{MODULE_F_NAME}}' },
  { listName: 'moduleLastName', name: 'Module Last Name', value: '{{MODULE_L_NAME}}' },
  { listName: 'moduleFullName', name: 'Module Full Name', value: '{{MODULE_NAME}}' },
  { listName: 'moduleEmail', name: 'Module Email', value: '{{MODULE_EMAIL}}' },
  { listName: 'userFirstName', name: 'User First Name', value: '{{USER_[username-goes-here]_NAME}}' },
  { listName: 'userLastName', name: 'User Last Name', value: '{{USER_[username-goes-here]_L_NAME}}' },
  { listName: 'userFullName', name: 'User Full Name', value: '{{USER_[username-goes-here]_NAME}}' },
  { listName: 'userEmail', name: 'User Email', value: '{{USER_[username-goes-here]_EMAIL}}' },
  { listName: 'targetFullName', name: 'Target Full Name', value: '{{TCNAME}}' },
  { listName: 'targetAbbreviatedName', name: 'Target Abbreviated Name', value: '{{TCNAMEABB}}' },
  { listName: 'targetAddress', name: 'Target Address', value: '{{TCADDRESS}}' },
  { listName: 'targetCity', name: 'Target City', value: '{{TCCITY}}' },
  { listName: 'targetState', name: 'Target State', value: '{{TCSTATE}}' },
  { listName: 'targetZip', name: 'Target Zip', value: '{{TCZIP}}' },
  { listName: 'targetCountry', name: 'Target Country', value: '{{TCCOUNTRY}}' },
  { listName: 'targetPhone', name: 'Target Phone', value: '{{TCPHONE}}' },
  { listName: 'targetRevenue', name: 'Target Revenue', value: '{{TCSALES}}' },
  { listName: 'targetDescription', name: 'Target Description', value: '{{TCDES}}' },
  { listName: 'targetWeb', name: 'Target Web', value: '{{TCWEB}}' },
  { listName: 'targetEmployees', name: 'Target Employees', value: '{{TCEMP}}' },
  { listName: 'targetDealmaker1', name: 'Target Dealmaker 1', value: '{{SECONDARY_DM}}' },
  { listName: 'targetIndustries', name: 'Target industries', value: '{{INDUSTRIES}}' },
  { listName: 'industry1', name: 'The 1st Target Industry', value: '{{INDUSTRY1}}' },
  { listName: 'industry2', name: 'The 2nd Target Industry', value: '{{INDUSTRY2}}' },
  { listName: 'industry3', name: 'The 3rd Target Industry', value: '{{INDUSTRY3}}' },
  { listName: 'industry4', name: 'The 4th Target Industry', value: '{{INDUSTRY4}}' },
  { listName: 'targetMarkets', name: 'Target Markets', value: '{{MARKETS}}' },
  { listName: 'market1', name: 'The 1st Target Market', value: '{{MARKET1}}' },
  { listName: 'market2', name: 'The 2nd Target Market', value: '{{MARKET2}}' },
  { listName: 'market3', name: 'The 3rd Target Market', value: '{{MARKET3}}' },
  { listName: 'market4', name: 'The 4th Target Market', value: '{{MARKET4}}' },
  { listName: 'targetProducts', name: 'Target Products', value: '{{PRODUCTS}}' },
  { listName: 'product1', name: 'The 1st Target Product', value: '{{PRODUCT1}}' },
  { listName: 'product2', name: 'The 2nd Target Product', value: '{{PRODUCT2}}' },
  { listName: 'product3', name: 'The 3rd Target Product', value: '{{PRODUCT3}}' },
  { listName: 'product4', name: 'The 4th Target Product', value: '{{PRODUCT4}}' },
  { listName: 'targetCustomers', name: 'Target Customers', value: '{{CUSTOMERS}}' },
  { listName: 'customer1', name: 'The 1st Target Customer', value: '{{CUSTOMER1}}' },
  { listName: 'customer2', name: 'The 2nd Target Customer', value: '{{CUSTOMER2}}' },
  { listName: 'customer3', name: 'The 3rd Target Customer', value: '{{CUSTOMER3}}' },
  { listName: 'customer4', name: 'The 4th Target Customer', value: '{{CUSTOMER4}}' },
  { listName: 'targetCompetitors', name: 'Target Competitors', value: '{{COMPETITORS}}' },
  { listName: 'competitor1', name: 'The 1st Target Competitor', value: '{{COMPETITOR1}}' },
  { listName: 'competitor2', name: 'The 2nd Target Competitor', value: '{{COMPETITOR2}}' },
  { listName: 'competitor3', name: 'The 3rd Target Competitor', value: '{{COMPETITOR3}}' },
  { listName: 'competitor4', name: 'The 4th Target Competitor', value: '{{COMPETITOR4}}' },
  { listName: 'parentFullName', name: 'Parent Full Name', value: '{{PCNAME}}' },
  { listName: 'parentAbbreviatedName', name: 'Parent Abbreviated Name', value: '{{PCNAMEABB}}' },
  { listName: 'parentDBA', name: 'Parent DBA', value: '{{PCNAMEDBA}}' },
  { listName: 'primaryContactPrefix', name: 'Primary Contact Prefix', value: '{{TCCEOSAL}}' },
  { listName: 'primaryContactFullName', name: 'Primary Contact Full Name', value: '{{TCFULLNAME}}' },
  { listName: 'primaryContactFirstName', name: 'Primary Contact First Name', value: '{{TCCEOFIRST}}' },
  { listName: 'primaryContactLastName', name: 'Primary Contact Last Name', value: '{{TCCEOLAST}}' },
  { listName: 'primaryContactSuffix', name: 'Primary Contact Suffix', value: '{{TCCEO_SUFFIX}}' },
  { listName: 'primaryContactTitle', name: 'Primary Contact Title', value: '{{TCCEOTITLE}}' },
  { listName: 'primaryContactEmail', name: 'Primary Contact Primary Email', value: '{{TCCEOEMAIL}}' },
];

const TemplateEditorPopupComponent = ({ popup }) => {
  const { closePopup } = usePopupContext();
  const editor = useRef(null);
  const [htmlSource] = useState(popup.getIn(['props', 'htmlSource']));
  const [content, setContent] = useState(htmlSource);

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: 'Start typings...',
      iframe: true,
      height: '70vh',
      maxHeight: '70vh',
      addNewLine: false,
      hidePoweredByJodit: true,
      width: '100%',
      extraButtons: [
        {
          name: 'Insert Variable',
          tooltip: 'Insert Variable',
          icon: 'menu',
          list: DROPDOWN_LIST.map(dropDown => dropDown.name),
          exec: (editor, command, control) => {
            setTimeout(() => {
              control.button.trigger.click();
            }, 1);

            const value = control.button.name;
            const foundListItem = DROPDOWN_LIST.find(dropDown => dropDown.name === value);

            if (foundListItem) {
              editor.s.insertHTML(`<span>${foundListItem.value}</span>`);
            }
          },
        },
      ],
    }),
    [],
  );

  const handleBlur = newContent => {
    setContent(newContent);
  };

  const handleSave = () => {
    const onSave = popup.getIn(['props', 'onSave']);

    onSave({
      formName: 'htmlSource',
      formData: content,
    });
    closePopup();
  };

  const footer = (
    <Button className="btn-primary" onClick={handleSave}>
      Save
    </Button>
  );

  return (
    <Popup className={style.host} footer={footer} header="Editor" id="templateEditorPopup">
      <JoditEditor ref={editor} config={config} onBlur={handleBlur} tabIndex={1} value={content} />
    </Popup>
  );
};

/** Template editor popup component. */
export const TemplateEditorPopup = memo(TemplateEditorPopupComponent);
